import { LinkIcon } from "lucide-react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Button } from "@/components/button";
import { Input, Label, TextField } from "@/components/field";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectListItemDescription,
  SelectListItemLabel,
  SelectPopover,
} from "@/components/select";
import { useGetProjectDetailsOptions } from "@/features/projects/project/details/hooks/useGetProjectDetailsOptions";

import { useProjectDetails } from "../useProjectDetails";

export function AccountDetailsSection() {
  const { control } = useFormContext();
  const { isReadOnly, projectId } = useProjectDetails();
  const website = useWatch({ control, name: "website" });
  const { data: options } = useGetProjectDetailsOptions(projectId);
  const csUsers = [...(options?.csUsers || [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  return (
    <div className="bg-bg-primary px-6 mt-5">
      <h3 className="text-lg  text-text-primary mb-3">Account Details</h3>
      <div className="grid grid-cols-2 gap-x-5 space-y-5">
        <Controller
          control={control}
          name="accountName"
          render={({ field, fieldState }) => (
            <TextField
              className="w-full"
              isDisabled={isReadOnly}
              isInvalid={!!fieldState.error}
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            >
              <Label>Account Name</Label>
              <Input />
            </TextField>
          )}
        />

        <div className="flex items-end gap-2">
          <Controller
            control={control}
            name="website"
            render={({ field, fieldState }) => (
              <TextField
                className="flex-1"
                isDisabled={isReadOnly}
                isInvalid={!!fieldState.error}
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value}
              >
                <Label>Website Address</Label>
                <Input />
              </TextField>
            )}
          />
          {website && (
            <Button
              className="mb-[2px]"
              isDisabled={!website}
              onPress={() =>
                window.open(
                  /^https?:\/\//i.test(website)
                    ? website
                    : `https://${website}`,
                  "_blank",
                )
              }
              variant="secondary"
            >
              <LinkIcon size={16} />
            </Button>
          )}
        </div>

        <Controller
          control={control}
          name="assignedCustomerSuccessId"
          render={({ field: { onChange, value } }) => (
            <div className="flex flex-col gap-1">
              <Label>Assigned Customer Success</Label>
              <Select
                isDisabled={isReadOnly}
                onSelectionChange={onChange}
                placeholder="Select CS"
                selectedKey={value}
              >
                <SelectButton />
                <SelectPopover>
                  <SelectListBox
                    items={csUsers.map((u) => ({
                      designation: u.designation,
                      id: Number(u.id),
                      name: u.name,
                    }))}
                  >
                    {(item) => (
                      <SelectListItem key={item.id} textValue={item.name}>
                        <div className="flex flex-col">
                          <SelectListItemLabel>{item.name}</SelectListItemLabel>
                          {item.designation && (
                            <SelectListItemDescription>
                              {item.designation}
                            </SelectListItemDescription>
                          )}
                        </div>
                      </SelectListItem>
                    )}
                  </SelectListBox>
                </SelectPopover>
              </Select>
            </div>
          )}
        />
      </div>
    </div>
  );
}
